import { type Ref, computed } from 'vue'
import type { ExtraSection, ExtraSectionPosition } from '@/types/extraSection'
import {
  applyBackgroundFillStyle,
  backgroundFillFromExtraSection,
} from '@/utils/backgroundGradient'

export function getSectionStyle(section: ExtraSection): Record<string, string> {
  if (sectionHasGalleryWidget(section)) {
    const style: Record<string, string> = {
      padding: '0',
      margin: '0',
      position: 'relative',
      borderRadius: '0',
    }

    if (section.transparent_background) {
      style.backgroundColor = 'transparent'
      return style
    }

    const bgType = section.background_type || 'color'
    if (bgType === 'pattern') {
      style.backgroundColor = section.background_color || 'transparent'
    } else {
      const fill = backgroundFillFromExtraSection(section)
      if (bgType === 'color' && !section.background_color) {
        fill.color = 'transparent'
      }
      applyBackgroundFillStyle(style, fill)
    }

    return style
  }

  const paddingClass: Record<string, string> = {
    small: '1rem',
    medium: '1.5rem',
    large: '2rem',
    xlarge: '2.5rem',
  }

  let padding: string
  if (
    section.section_padding !== undefined &&
    section.section_padding !== null &&
    section.section_padding !== ''
  ) {
    const paddingValue =
      typeof section.section_padding === 'number'
        ? section.section_padding
        : parseInt(String(section.section_padding))
    padding = `${paddingValue}px`
  } else {
    padding = section.transparent_background
      ? '0.5rem'
      : paddingClass[section.section_size || 'medium'] || '0.5rem'
  }

  const style: Record<string, string> = {
    padding,
    position: 'relative',
  }

  if (
    section.border_radius !== undefined &&
    section.border_radius !== null &&
    section.border_radius !== ''
  ) {
    const radiusValue =
      typeof section.border_radius === 'number'
        ? section.border_radius
        : parseInt(String(section.border_radius))
    style.borderRadius = radiusValue > 0 ? `${radiusValue}px` : '0px'
  } else {
    style.borderRadius = '20px'
  }

  if (section.section_margin !== undefined) {
    style.margin = `${section.section_margin} 0`
  } else {
    style.margin = '3rem 0'
  }

  if (section.box_shadow !== false) {
    style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.3)'
  }

  if (section.backdrop_blur !== false) {
    style.backdropFilter = 'blur(15px)'
    style.webkitBackdropFilter = 'blur(15px)'
  }

  if (section.transparent_background) {
    style.backgroundColor = 'transparent'
    delete style.boxShadow
    delete style.backdropFilter
    delete style.webkitBackdropFilter
    return style
  }

  const bgType = section.background_type || 'color'

  if (bgType === 'pattern') {
    style.backgroundColor = section.background_color || '#f8f9fa'
  } else {
    const fill = backgroundFillFromExtraSection(section)
    if (bgType === 'color' && !section.background_color) {
      fill.color = 'rgba(255, 255, 255, 0.8)'
    }
    applyBackgroundFillStyle(style, fill)
  }

  return style
}

export function getSectionImageUrl(imagePath: string) {
  if (!imagePath) return ''
  return imagePath.startsWith('http') ? imagePath : `/backend/uploads/sections/${imagePath}`
}

export function getSectionImageColumnClass(section: ExtraSection) {
  if (!section.gallery || section.gallery.length === 0) {
    return section.image ? 'col-lg-6 col-md-6 mb-4 mb-md-0' : ''
  }

  switch (section.layout) {
    case 'image_left':
    case 'image_right':
    case 'gallery_grid':
    case 'gallery_carousel':
      return 'col-lg-6 col-md-6 mb-4 mb-md-0'
    case 'image_top':
      return 'col-12 mb-4'
    default:
      return 'col-lg-6 col-md-6 mb-4 mb-md-0'
  }
}

export function getSectionContentColumnClass(section: ExtraSection) {
  const hasImages = (section.gallery && section.gallery.length > 0) || section.image

  if (!hasImages || section.layout === 'text_only' || section.layout === 'page_builder') {
    return 'col-12'
  }

  switch (section.layout) {
    case 'image_left':
    case 'image_right':
    case 'gallery_grid':
    case 'gallery_carousel':
      return 'col-lg-6 col-md-6'
    case 'image_top':
      return 'col-12'
    case 'products_grid':
    case 'products_carousel':
      return 'col-12'
    default:
      return hasImages ? 'col-lg-6 col-md-6' : 'col-12'
  }
}

export function getGalleryGridClass(totalImages: number, index: number) {
  if (totalImages === 1) return 'col-12'
  if (totalImages === 2) return 'col-6'
  if (totalImages === 3) return index === 0 ? 'col-12' : 'col-6'
  if (totalImages === 4) return 'col-6'
  if (totalImages >= 5) return index < 2 ? 'col-6' : 'col-4'
  return 'col-4'
}

export function getTitleStyle(section: ExtraSection) {
  return { color: section.title_color || '#212529' }
}

export function sectionHasGalleryWidget(section: ExtraSection): boolean {
  return (
    Array.isArray(section.widgets) &&
    section.widgets.some((widget) => widget.type === 'gallery')
  )
}

export function hasGalleryWidgetAtGalleryPositions(sections: ExtraSection[]): boolean {
  if (!sections?.length) return false
  return sections.some(
    (section) =>
      section.is_active &&
      (section.position === 'before_gallery' || section.position === 'after_gallery') &&
      sectionHasGalleryWidget(section),
  )
}

export function filterActiveSectionsByPosition(
  sections: ExtraSection[],
  position: ExtraSectionPosition | string,
) {
  if (!sections || !Array.isArray(sections)) return []
  return sections.filter((section) => section.is_active && section.position === position)
}

export function useExtraSections(extraSections: Ref<ExtraSection[]>) {
  const getActiveSectionsByPosition = (position: ExtraSectionPosition | string) =>
    filterActiveSectionsByPosition(extraSections.value, position)

  const activeBeforeGallery = computed(() => getActiveSectionsByPosition('before_gallery'))
  const activeAfterGallery = computed(() => getActiveSectionsByPosition('after_gallery'))
  const activeBeforeStore = computed(() => getActiveSectionsByPosition('before_store'))
  const activeAfterStore = computed(() => getActiveSectionsByPosition('after_store'))
  const activeBeforeProducts = computed(() => getActiveSectionsByPosition('before_products'))
  const activeAfterProducts = computed(() => getActiveSectionsByPosition('after_products'))
  const activeFooter = computed(() => getActiveSectionsByPosition('footer'))

  return {
    getActiveSectionsByPosition,
    activeBeforeGallery,
    activeAfterGallery,
    activeBeforeStore,
    activeAfterStore,
    activeBeforeProducts,
    activeAfterProducts,
    activeFooter,
    getSectionStyle,
    getSectionImageUrl,
    getSectionImageColumnClass,
    getSectionContentColumnClass,
    getGalleryGridClass,
    getTitleStyle,
    sectionHasGalleryWidget,
    hasGalleryWidgetAtGalleryPositions,
  }
}
